import type { LifecycleState, OperatingLoad } from "@/core/tma";

export interface LoadViewFilters {
  lifecycleStates?: LifecycleState[];
  customerNames?: string[];
  carrierNames?: string[];
  equipmentTypes?: string[];
  exceptionOnly?: boolean;
  search?: string;
}

export interface LoadViewSort {
  field: "reference" | "pickupWindowStart" | "deliveryWindowStart" | "risk";
  direction: "asc" | "desc";
}

export interface LoadViewLoad extends OperatingLoad {
  exceptionCount?: number;
  currentRiskScore?: number;
}

export function applyLoadView(
  loads: LoadViewLoad[],
  filters: LoadViewFilters,
  sort: LoadViewSort = { field: "pickupWindowStart", direction: "asc" },
) {
  return [...loads.filter((load) => matchesFilters(load, filters))].sort((left, right) => {
    const direction = sort.direction === "asc" ? 1 : -1;
    return compareBySort(left, right, sort.field) * direction;
  });
}

export function buildFilterSummary(filters: LoadViewFilters) {
  return [
    ...(filters.lifecycleStates ?? []),
    ...(filters.customerNames ?? []),
    ...(filters.carrierNames ?? []),
    ...(filters.equipmentTypes ?? []),
    filters.exceptionOnly ? "exceptions" : undefined,
    filters.search,
  ]
    .filter(Boolean)
    .join(" / ");
}

export function togglePinnedLoad(pinnedLoadIds: string[], loadId: string) {
  return pinnedLoadIds.includes(loadId)
    ? pinnedLoadIds.filter((candidate) => candidate !== loadId)
    : [...pinnedLoadIds, loadId];
}

function matchesFilters(load: LoadViewLoad, filters: LoadViewFilters) {
  if (filters.lifecycleStates?.length && !filters.lifecycleStates.includes(load.lifecycleState)) return false;
  if (filters.customerNames?.length && !filters.customerNames.includes(load.customerName)) return false;
  if (filters.carrierNames?.length && !filters.carrierNames.includes(load.carrierName)) return false;
  if (filters.equipmentTypes?.length && !filters.equipmentTypes.includes(load.equipmentType)) return false;
  if (filters.exceptionOnly && (load.exceptionCount ?? 0) === 0) return false;
  if (filters.search) {
    const haystack = [
      load.reference,
      load.customerName,
      load.carrierName,
      load.origin.city,
      load.origin.state,
      load.destination.city,
      load.destination.state,
    ]
      .join(" ")
      .toLowerCase();
    if (!haystack.includes(filters.search.toLowerCase())) return false;
  }
  return true;
}

function compareBySort(left: LoadViewLoad, right: LoadViewLoad, field: LoadViewSort["field"]) {
  if (field === "reference") return left.reference.localeCompare(right.reference);
  if (field === "risk") return (left.currentRiskScore ?? 0) - (right.currentRiskScore ?? 0);
  return Date.parse(left[field]) - Date.parse(right[field]);
}
